import type { Table } from '@tanstack/react-table'
import React from 'react'
import styled from 'styled-components'

import { useColumnSizeVars } from '../hooks/useColumnSizeVars'
import { useScrollingAreaDimensions } from '../hooks/useDataTableDimensions'
import { ElementVirtualizer, type ElementVirtualizerType } from './ElementVirtualizer'
import { WindowVirtualizer, type WindowVirtualizerType } from './WindowVirtualizer'

export type VirtualizerType = ElementVirtualizerType | WindowVirtualizerType

type Children = (
  columnSizeVars: Record<string, number>,
  virtualizer?: ElementVirtualizerType | WindowVirtualizerType
) => React.ReactNode

export interface VirtualizerProps<TData> {
  'data-e2e'?: string
  children: Children
  enabled?: boolean
  estimatedRowHeight: number
  isWindow?: boolean
  rowsLength: number
  scrollMargin?: number
  table: Table<TData>
}

export const HorizontalScrollingArea = styled.div<{ height: number }>`
  max-width: calc(100% - 1px);
  width: calc(100% - 1px);

  &[data-enabled='true'] {
    padding-right: ${props => props.theme.spacings.medium};
    margin-left: ${props => props.theme.spacings.medium};
    max-width: calc(100% - ${props => props.theme.spacings.medium});
    width: calc(100% - ${props => props.theme.spacings.medium});
    height: ${props => props.height}px;
    overflow-y: auto;
    overscroll-behavior: contain;
    margin-bottom: -${props => props.theme.spacings.medium};

    &[data-window='true'] {
      height: initial;
      overscroll-behavior: initial;
      overflow-y: initial;
      overflow-x: auto;
      margin-bottom: 0;
    }
  }
`

export const Virtualizer = <TData,>({
  'data-e2e': dataE2e = 'virtualizer',
  children,
  enabled = true,
  estimatedRowHeight,
  isWindow = false,
  rowsLength,
  scrollMargin,
  table,
}: VirtualizerProps<TData>) => {
  const { setHorizontalScrollingAreaRef, horizontalScrollingArea, dimensions } =
    useScrollingAreaDimensions()

  const columnSizeVars = useColumnSizeVars({
    horizontalScrollingAreaWidth: dimensions.width,
    table,
  })

  const Virtualize = isWindow ? WindowVirtualizer : ElementVirtualizer

  return (
    <HorizontalScrollingArea
      data-e2e={`${dataE2e}--scrolling-area`}
      data-enabled={enabled}
      data-window={isWindow}
      height={dimensions.height}
      ref={setHorizontalScrollingAreaRef}
    >
      {enabled ? (
        <Virtualize
          estimatedRowHeight={estimatedRowHeight}
          scrollElement={horizontalScrollingArea}
          rowsLength={rowsLength}
          scrollMargin={scrollMargin}
        >
          {rowVirtualizer => children(columnSizeVars, rowVirtualizer)}
        </Virtualize>
      ) : (
        children(columnSizeVars)
      )}
    </HorizontalScrollingArea>
  )
}

export default Virtualizer
